import { useCallback, useRef } from "react";

import { uuidV4 } from "./uuid";

// Idempotency-Key plumbing.
//
// Plan §"Idempotency UX":
// - one fresh UUID per logical action (form open / first focus)
// - reuse the same key on retry of the same payload
// - regenerate when the payload changes (otherwise the server returns
//   `409 idempotency_conflict`)
//
// The store is keyed by a stable payload signature. Callers compute the
// signature themselves — typically `JSON.stringify(payload)` for small forms.

export function useIdempotencyKey(): {
  keyFor: (payloadSignature: string) => string;
  reset: () => void;
} {
  const cache = useRef<{ signature: string; key: string } | null>(null);

  const keyFor = useCallback((payloadSignature: string): string => {
    if (cache.current !== null && cache.current.signature === payloadSignature) {
      return cache.current.key;
    }
    const next = { signature: payloadSignature, key: uuidV4() };
    cache.current = next;
    return next.key;
  }, []);

  const reset = useCallback(() => {
    cache.current = null;
  }, []);

  return { keyFor, reset };
}
